import type { FilterConditionDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { formatDate, formatTime } from "@/shared/lib/date-time";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
  hydrateLookupItems,
} from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { FormInfiniteMultiCombobox } from "@/shared/ui/FormInfiniteMultiCombobox";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormInput,
  FormRoot,
  RHF,
  ScrollArea,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  zodResolver,
} from "@devhop/ui";
import { Loader2Icon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import type { BulkUpdateClassSessionInstructorsPreviewResultDTO } from "../../domain/contracts/sessions";
import {
  useBulkUpdateClassSessionInstructors,
  usePreviewBulkUpdateClassSessionInstructors,
} from "../api/queries";
import { useClassSessionFilterDisplay } from "../lib/class-session-filter-display";
import { instructorsFromLeadAssist } from "../lib/session-instructor-form";

type InstructorItem = { id: string; name: string };

const FormSchema = z.object({
  leadInstructorId: z.string().min(1, "ຕ້ອງເລືອກຄູຫຼັກ"),
  assistInstructorIds: z.array(z.string()).default([]),
  changeReason: z.string().min(1, "ຕ້ອງກຳນົດເຫດຜົນ"),
});

type BulkUpdateClassSessionInstructorsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: FilterConditionDTO[];
};

export function BulkUpdateClassSessionInstructorsDialog({
  open,
  onOpenChange,
  filters,
}: BulkUpdateClassSessionInstructorsDialogProps) {
  const previewMutation = usePreviewBulkUpdateClassSessionInstructors();
  const bulkMutation = useBulkUpdateClassSessionInstructors();
  const [preview, setPreview] =
    useState<BulkUpdateClassSessionInstructorsPreviewResultDTO | null>(null);
  const { items: filterItems, isLoading: filterLabelsLoading } =
    useClassSessionFilterDisplay(filters);

  const methods = RHF.useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      leadInstructorId: "",
      assistInstructorIds: [],
      changeReason: "",
    },
  });

  const leadInstructorId = methods.watch("leadInstructorId");
  const assistInstructorIds = methods.watch("assistInstructorIds");

  const instructorsPayload = useMemo(
    () => instructorsFromLeadAssist(leadInstructorId, assistInstructorIds ?? []),
    [leadInstructorId, assistInstructorIds],
  );

  useEffect(() => {
    if (!open) {
      methods.reset();
      setPreview(null);
    }
  }, [open, methods.reset]);

  const filtersKey = JSON.stringify(filters);
  const instructorsKey = JSON.stringify(instructorsPayload);

  useEffect(() => {
    if (!open || instructorsPayload.length === 0) {
      setPreview(null);
      return;
    }
    const timer = setTimeout(() => {
      previewMutation.mutate(
        { filters, instructors: instructorsPayload },
        {
          onSuccess: (data) => setPreview(data),
          onError: () => setPreview(null),
        },
      );
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filtersKey / instructorsKey stabilize deps
  }, [open, instructorsKey, filtersKey]);

  const onSubmit = methods.handleSubmit(async (vals) => {
    const instructors = instructorsFromLeadAssist(
      vals.leadInstructorId,
      vals.assistInstructorIds ?? [],
    );
    await bulkMutation.mutateAsync({
      filters,
      instructors,
      changeReason: vals.changeReason.trim(),
    });
    onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ອັບເດດຄູສອນ (Bulk)</DialogTitle>
          <DialogDescription>
            ປ່ຽນຄູຫຼັກ/TA ສຳລັບຊົ່ວໂມງຮຽນທີ່ຈອງແລ້ວຕາມຕົວກອງ — ຄລາດສ໌ Partner ໃຊ້ຄູຫຼັກຄົນດຽວ (ບໍ່ມີ TA)
          </DialogDescription>
        </DialogHeader>

        <FormRoot methods={methods} onSubmit={onSubmit} className="space-y-4">
          <div className="rounded-lg border bg-muted/30 p-3">
            <p className="mb-2 font-medium text-sm">ຕົວກອງປັດຈຸບັນ</p>
            {filterItems.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                ບໍ່ມີຕົວກອງ — ກະລຸນາກັ່ນຕອງກ່ອນ
              </p>
            ) : filterLabelsLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Loader2Icon className="size-4 animate-spin" />
                ກຳລັງໂຫຼດ...
              </div>
            ) : (
              <dl className="grid gap-2 sm:grid-cols-2">
                {filterItems.map((item) => (
                  <div key={item.label} className="text-sm">
                    <dt className="text-muted-foreground">{item.label}</dt>
                    <dd className="font-medium">{item.value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormInfiniteCombobox<InstructorItem>
              name="leadInstructorId"
              label="ຄູຫຼັກໃໝ່"
              placeholder="ເລືອກຄູຫຼັກ"
              requiredMark
              queryKey={["master", "instructors", "lookup", "bulk-lead"]}
              queryFn={({ search, pageParam }) =>
                fetchLookupForInfinite<InstructorItem>(
                  `${config.apiUrl}/master/instructors/lookup`,
                  { search, pageParam },
                )
              }
              preloadQueryFn={(id) =>
                hydrateLookupItem<InstructorItem>(
                  `${config.apiUrl}/master/instructors/lookup`,
                  id,
                )
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              clearable
            />
            <FormInfiniteMultiCombobox<InstructorItem>
              name="assistInstructorIds"
              label="ຄູຊ່ວຍ (TA)"
              placeholder="ເລືອກຄູຊ່ວຍ ສູງສຸດ 3 ຄົນ"
              queryKey={["master", "instructors", "lookup", "bulk-assist"]}
              queryFn={({ search, pageParam }) =>
                fetchLookupForInfinite<InstructorItem>(
                  `${config.apiUrl}/master/instructors/lookup`,
                  { search, pageParam },
                )
              }
              preloadQueryFn={(ids) =>
                hydrateLookupItems<InstructorItem>(
                  `${config.apiUrl}/master/instructors/lookup`,
                  ids,
                )
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
            />
          </div>

          <FormInput
            name="changeReason"
            label="ເຫດຜົນການປ່ຽນແປງ"
            placeholder="ຕົວຢ່າງ: ປ່ຽນຄູຕາມຕາຕະລາງໃໝ່"
            requiredMark
          />

          {leadInstructorId && (
            <div className="space-y-3 rounded-lg border bg-muted/30 p-3">
              <p className="font-medium text-sm">ສະຫຼຸບການອັບເດດ</p>
              {previewMutation.isPending ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2Icon className="size-4 animate-spin" />
                  ກຳລັງຄິດຄຳນວນ...
                </div>
              ) : preview ? (
                <>
                  <ul className="space-y-1 text-muted-foreground text-sm">
                    <li>
                      ຕົງກັບຕົວກອງ:{" "}
                      <span className="font-medium text-foreground">
                        {preview.totalMatching.toLocaleString("lo-LA")}{" "}
                        ຊົ່ວໂມງຮຽນ
                      </span>
                    </li>
                    <li>
                      ຈອງແລ້ວ:{" "}
                      <span className="font-medium text-foreground">
                        {preview.scheduledMatching.toLocaleString("lo-LA")}{" "}
                        ຊົ່ວໂມງຮຽນ
                      </span>
                    </li>
                    <li>
                      ຈະອັບເດດ:{" "}
                      <span className="font-medium text-foreground">
                        {preview.updatableCount.toLocaleString("lo-LA")}{" "}
                        ຊົ່ວໂມງຮຽນ
                      </span>
                    </li>
                  </ul>

                  {preview.updatableCount === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      ບໍ່ມີຊົ່ວໂມງຮຽນທີ່ຕ້ອງອັບເດດ (ຄູເດີມຕົງກັນແລ້ວ ຫຼືບໍ່ມີສະຖານະຈອງແລ້ວ)
                    </p>
                  ) : preview.sessions.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-muted-foreground text-sm">
                        ລາຍການທີ່ຈະອັບເດດ
                        {preview.sessionsTruncated
                          ? ` (ສະແດງ ${preview.sessions.length.toLocaleString("lo-LA")} ຈາກ ${preview.updatableCount.toLocaleString("lo-LA")})`
                          : ""}
                      </p>
                      <ScrollArea className="h-[240px] rounded-md border bg-background">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>ວັນທີ</TableHead>
                              <TableHead>ເວລາ</TableHead>
                              <TableHead>ຄລາດສ໌</TableHead>
                              <TableHead>ຄູປັດຈຸບັນ</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {preview.sessions.map((session) => (
                              <TableRow key={session.id}>
                                <TableCell className="whitespace-nowrap">
                                  {formatDate(session.date)}
                                </TableCell>
                                <TableCell className="whitespace-nowrap">
                                  {formatTime(session.startTime)} –{" "}
                                  {formatTime(session.endTime)}
                                </TableCell>
                                <TableCell>{session.templateName}</TableCell>
                                <TableCell>{session.instructorNames}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="text-muted-foreground text-sm">
                  ບໍ່ສາມາດຄິດຄຳນວນໄດ້
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="submit"
              disabled={
                bulkMutation.isPending ||
                !leadInstructorId ||
                !preview ||
                preview.updatableCount === 0
              }
            >
              {bulkMutation.isPending ? "ກຳລັງອັບເດດ..." : "ອັບເດດຄູສອນ"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
